import * as THREE from 'three';
import { EG, enemyPalette } from '../entities/enemy-parts';
import { camera, mesh } from './scene';
import { damp } from '../core/math';
import { codexGroup } from './codex-scene';
import {
  armCodexBossDeath, resetCodexBossDeath, stepCodexBossDeath
} from './codex-boss-death';
import { place, setFitTarget } from './codex-fit';
/* ============================================================
   圖鑑展示台上的那一隻
   ============================================================

   造型表（`entities/families/*.ts`）有兩條輸出路徑（規格 §6.2）：
   戰場上是 `InstancedMesh`，這裡是**真的 mesh 階層** —— 一次只有一隻，
   而且要能拉近看分面，實例化在這裡沒有好處。

   ## 三個這一層負責的東西

   1. **自動 fit。** 圖鑑要放的東西從 0.86 到 48 單位差五十五倍。
      鏡頭距離從實際體型反算，所以新移植進來的東西不用調任何參數。
   2. **比例尺機體。** 旁邊永遠站一台半透明的 CHASSIS-0（使用者 2026-08-03
      再次指定：「不要用比例尺，換回我的機甲在旁邊對照」）。沒有參照的話
      「這隻有多大」在螢幕上是讀不出來的 —— 每一張都填滿畫面。
      代價是橫向取景要同時容得下它，所以小型敵人不可能佔滿畫面（見 `place()`）。
   3. **三段解鎖的視覺。** 沒看過＝空台；看過沒打死＝**會動的全黑剖面**
      （動作看得到、身分看不到）；打死過＝完整造型。
      黑影用的是同一份 part 表與同一份動畫，只換材質。

   ⚠️ **體型讀的是「戰場上那把尺」。** 圖鑑裡的大小要跟戰場上看到的一樣，
   否則玩家在圖鑑裡建立的體型印象一進遊戲就作廢了。倍率**不在這裡算** ——
   `data/codex-list.ts` 建表時就算好 `T.bodyK`（小怪 ×2、大型 BOSS 走
   `bossFieldScale` 壓縮），這裡只讀欄位。2026-08-03 之前這裡寫死 `BODY_K`，
   於是圖鑑的 KAISER 是 48 而戰場上是 15，差 3.2 倍。 */

var EGMAP = { box: EG.box, cyl: EG.cyl, bev: EG.bev, cone: EG.cone, oct: EG.oct, tor: EG.tor };
var K_BEV = 1 / Math.cos(Math.PI / 8);

/** 黑影用的材質。**不受光**（`MeshBasicMaterial`）—— 受光的黑會被
    緣光描出邊，那就洩漏了輪廓細節，而第二段的重點正是「你只知道形狀」。 */
var SIL_MAT = new THREE.MeshBasicMaterial({ color: 0x05070c });

/** 精英的材質映射（規格 §2.4：armor 亮一階，形狀不變）。 */
var EL_MAT = { armor: 'armorE', glow: 'glowE' };

var host = new THREE.Group();
codexGroup.add(host);
var live: any[] = [], cur: any = null;

/** 目前這一隻的世界高度（含 `BODY_K`）。鏡頭距離讀它。 */
export var modelH = 2;
/** 目前這一隻的世界**寬度**（含 `BODY_K`）。橫向取景讀它 ——
    只算高度的話 KATHEDRALE（22m 寬）與 SCHLANGE（44m 寬）會直接溢出畫面兩側，
    而 headless 完全看不出來（第一版就是這樣，實看截圖才發現）。 */
export var modelW = 2;
var _box = new THREE.Box3(), _probe = new THREE.Box3();
var _bs = new THREE.Vector3(), _bc = new THREE.Vector3();

/** 建一隻。`opt` = { elite, vi（變體索引）, sil（黑影） }。 */
var curT: any = null, curOpt: any = null;
export function setCodexModel(T, opt) {
  resetCodexBossDeath();
  while (host.children.length) host.remove(host.children[0]);
  live = []; cur = null;
  curT = T; curOpt = opt;
  if (!T) { modelH = 2; return; }

  /* ---------- BOSS：走真 rig（`buildXxx()` + `updXxx()`），不是 part 表 ----------

     小怪是「part 表 + `fn(P,o)`」，沒有階層；BOSS 場上一次只有一隻，
     付得起巢狀骨段、IK 與獨立追瞄的砲塔，所以造型與動畫都是自己的函式。
     圖鑑這裡把它原封不動跑起來 —— 移植對不對只能這樣看。 */
  if (T.boss) {
    var broot = T.build();
    // 尺寸是清單建表時算好的（`data/codex-list.ts`），這裡不重算
    var bsc = T.bodyK;
    broot.scale.setScalar(bsc);
    if (opt.sil) broot.traverse(function (o: any) { if (o.isMesh) o.material = SIL_MAT; });
    host.add(broot);

    /* **取景量的是實際包圍盒，不是欄位表的 `h`/`w`。**
       23 隻是手搭的階層，原點各自在不同的地方 —— SCHLANGE 的 13 節是
       從原點往 z 方向長出去的，照 `w:22` 對著原點取景等於對著牠的尾巴，
       畫面上會幾乎空白（第一版就是這樣，實看才發現）。

       量完把它**水平置中到展示台上**（只動 x/z，不動 y，否則浮空的
       ZENIT / MUTTER 會被壓到地上）。這是展示用的擺位，不是改造型。 */
    var rig0 = broot.userData.rig;
    broot.updateMatrixWorld(true);
    _box.setFromObject(broot);
    _box.getSize(_bs); _box.getCenter(_bc);
    broot.position.x -= _bc.x; broot.position.z -= _bc.z;

    cur = { T: T, g: broot, rig: rig0, sc: bsc, boss: 1, stg: opt.stg || 0 };
    resetLoop();
    modelH = Math.max(1.6, _bs.y);
    /* 水平取用對角線：13 節的蛇從哪個角度看都不只 `sx` 那麼寬。 */
    modelW = Math.hypot(_bs.x, _bs.z);
    setFitTarget(modelH, modelW, _bc.y);
    place();
    return;
  }

  var pal = enemyPalette(T.tint);
  var parts = T.parts.slice();
  if (opt.elite && T.ex) {
    parts = parts.map(function (q) { return { g: q.g, m: EL_MAT[q.m] || q.m, s: q.s, p: q.p, r: q.r, fn: q.fn }; });
    parts = parts.concat(T.ex);
  }
  var g = new THREE.Group();
  for (var i = 0; i < parts.length; i++) {
    var q = parts[i];
    var mat = opt.sil ? SIL_MAT : pal[q.m];
    var m = mesh(EGMAP[q.g], mat, q.s.slice(), q.p.slice(), q.r ? q.r.slice() : undefined);
    /* bev 的 x/z 量到角不是量到面 —— 與機體同一份規格（K_BEV）。 */
    if (q.g === 'bev') { m.scale.x *= K_BEV; m.scale.z *= K_BEV; }
    m.castShadow = !opt.sil; m.receiveShadow = !opt.sil;
    m.userData.base = { s: m.scale.clone(), p: m.position.clone(), r: m.rotation.clone() };
    m.userData.fn = q.fn;
    g.add(m); live.push(m);
  }
  var V = T.vars && T.vars[opt.vi || 0];
  var sc = T.bodyK * (V ? V.sc : 1) * (opt.elite ? 1.34 : 1);
  g.scale.setScalar(sc);
  host.add(g);

  /* 變體只改比例參數（`q`），不加 primitive、不改顏色（規格 §5.3）。 */
  var q0 = {};
  for (var k in T.q0) q0[k] = T.q0[k];
  if (V) for (var j in V.q) q0[j] = V.q[j];

  cur = { T: T, g: g, q: q0, sc: sc };
  resetLoop();
  modelH = (T.fly ? T.fly + 0.7 : T.h) * sc;
  modelW = T.w * sc;
  setFitTarget(modelH, modelW, 0);
  place();
}

/** 包圍盒可用嗎（無限大＝某些幾何沒設 boundingBox，量不到）。 */
function finiteBox(b) {
  return !b.isEmpty()
    && isFinite(b.min.x) && isFinite(b.max.x) && isFinite(b.min.y)
    && isFinite(b.max.y) && isFinite(b.min.z) && isFinite(b.max.z);
}

/* ---------- 展示循環 ---------- */

/** 展示用的狀態機。**與戰場同一套四段**（`sim/enemy-ai.ts`）：
    巡航 → 預備 → 攻擊 → 恢復，時間用該隻自己的 `tTel`/`tAtk`/`tRec`。

    ⚠️ 舊版是一條 3.4 秒的固定斜坡：`tel` 在 30% 處衝到 1、42% 之後
    **直接歸零**。那是小怪 1:1 那一輪在戰場端修掉的同一個缺陷，
    圖鑑這一支漏掉了 —— 症狀是「循環切在奇怪的地方」：蓄力滿了之後
    啪一下彈回，而且每一隻的長度都一樣（跟牠實際的預備時間無關）。
    現在恢復期用**指數平滑**退回 0（λ 8 / 10，與戰場同值）。

    **相位只在巡航推進**，出手時腿不再繼續走 —— 不然預備動作會被步態蓋掉。
    冷卻是固定值（戰場是隨機的）：圖鑑是展示，每次進來都該長得一樣。 */
var A = { st: 0, stT: 0, tel: 0, atk: 0, ph: 0, cd: 0, die: 0, dieOn: false };
function resetLoop() {
  A.st = 0; A.stT = 0; A.tel = 0; A.atk = 0; A.ph = 0; A.cd = 0.35; A.die = 0;
}

/** 死亡塌陷（開發模式）。**與戰場同一組數字**（`render/enemy-field.ts`
    的 `stepCorpses`）：`die` 每秒 +1.7、尺度縮到 12%、前傾 0.9·die。

    ⚠️ 原型的死亡分支**在呼叫 `pose()` 之前就 return** —— 塌下去的姿勢只有
    那兩個值，不疊型別自己的 pose，否則走路的擺動會一路演到屍體上。
    這裡照抄那個順序。演完停 0.7 秒再從頭來，不然一秒閃過去看不清楚。 */
export function setCodexDie(on) {
  A.dieOn = !!on;
  A.die = 0;
  resetCodexBossDeath();
  if (!on) resetLoop();
}
export function codexDieOn() { return A.dieOn; }

function stepLoop(T, dt) {
  if (A.dieOn) {
    A.die += dt * 1.7;
    if (A.die >= 1.9) A.die = 0;           // 演完停一下再重播
    A.tel = 0; A.atk = 0;
    return;
  }
  var tTel = T.tTel > 0 ? T.tTel : 0.7;
  var tAtk = T.tAtk > 0 ? T.tAtk : 0.22;
  var tRec = T.tRec > 0 ? T.tRec : 0.45;
  if (A.st === 0) {
    A.ph = (A.ph + dt * (T.gait || 1) * 1) % 1;
    A.tel = damp(A.tel, 0, 9, dt);
    A.atk = damp(A.atk, 0, 11, dt);
    A.cd -= dt;
    if (A.cd <= 0) { A.st = 1; A.stT = tTel; }
  } else if (A.st === 1) {
    A.stT -= dt;
    A.tel = 1 - Math.max(0, A.stT) / tTel;
    if (A.stT <= 0) { A.st = 2; A.stT = tAtk; A.tel = 1; }
  } else if (A.st === 2) {
    A.stT -= dt;
    A.atk = 1 - Math.max(0, A.stT) / tAtk;
    if (A.stT <= 0) { A.st = 3; A.stT = tRec; A.atk = 1; }
  } else {
    A.stT -= dt;
    A.tel = damp(A.tel, 0, 8, dt);
    A.atk = damp(A.atk, 0, 10, dt);
    if (A.stT <= 0) { A.st = 0; A.cd = 1.1; }
  }
}

/** 每個畫面幀。動畫通道 `o` 與戰場上是同一份（規格 §6.2），
    所以家族檔不知道自己在圖鑑裡跑還是在戰場上跑。 */
var lastT = 0;
export function tickCodexModel(t) {
  var dt = Math.min(0.05, Math.max(0, t - lastT));
  lastT = t;
  if (!cur) return;
  var T = cur.T;
  stepLoop(T, dt);
  var die = A.dieOn ? Math.min(1, A.die) : 0;
  var o: any = {
    t: t, ph: A.ph, mv: A.st === 0 ? 1 : 0, tel: A.tel, atk: A.atk,
    hit: 0, die: die, p2: 0, q: cur.q, sc: cur.sc, bank: 0,
    ry: 0, rp: 0, rr: 0, rz: 0
  };
  /* BOSS：`updXxx(rig, o, dt)` 自己會寫進 rig 的每一節，這裡只負責餵通道。
     `aim`/`yaw` 給獨立追瞄的砲塔用（原型是「敵人朝著原點」，圖鑑裡
     展品站在原點，所以讓瞄準方向繞著它慢慢轉，砲塔才看得出來在追）。 */
  if (cur.boss) {
    o.stg = cur.stg; o.p2 = cur.stg > 0 ? 1 : 0; o.broken = 0;
    o.yaw = 0; o.aim = Math.sin(t * 0.25) * 0.9;
    o.mv = 1; o.ph = (t * (T.gait || 1) * 0.22) % 1;
    /* BOSS 的死亡是一整段編排（原型 §12 移植），不是小怪那個「塌下去 + 縮小」。
       ⚠️ **炸裂之前 rig 還要繼續跑正常動畫** —— 抖動與吸氣是疊在上面的。
       炸裂會把 rig 拆成自由剛體，所以演完只能整隻重建（重建就重播）。 */
    if (A.dieOn) {
      armCodexBossDeath(cur.g, T, cur.sc);
      var D = stepCodexBossDeath(dt, cur.g);
      if (D.done) { setCodexModel(curT, curOpt); return; }
      if (D.pre && T.upd) T.upd(cur.rig, o, dt * D.scale);
      return;
    }
    if (T.upd) T.upd(cur.rig, o, dt);
    return;
  }
  /* 死亡塌陷：**不跑 pose**（原型在 pose 之前 return），把每一顆零件
     還原成建構姿勢，只在群組上演塌陷。 */
  if (die > 0) {
    for (var d = 0; d < live.length; d++) {
      var md_ = live[d], bb = md_.userData.base;
      md_.scale.copy(bb.s); md_.position.copy(bb.p); md_.rotation.copy(bb.r);
    }
    cur.g.scale.setScalar(cur.sc * (1 - 0.88 * die));
    cur.g.position.set(0, 0, 0);
    cur.g.rotation.set(0.9 * die, 0, 0);
    return;
  }
  cur.g.scale.setScalar(cur.sc);
  if (T.pose) T.pose(o);
  for (var i = 0; i < live.length; i++) {
    var m = live[i], b = m.userData.base;
    var P: any = {
      sx: b.s.x, sy: b.s.y, sz: b.s.z, x: b.p.x, y: b.p.y, z: b.p.z,
      rx: b.r.x, ry: b.r.y, rz: b.r.z
    };
    if (m.userData.fn) m.userData.fn(P, o);
    m.scale.set(P.sx, P.sy, P.sz);
    m.position.set(P.x, P.y, P.z);
    m.rotation.set(P.rx, P.ry, P.rz);
  }
  /* pose 寫在群組上（跟 Field 的 `_root` 同一個位置），
     `o.rz` 是沿朝向的位移不是旋轉 —— 名字是規格訂的，不要改。

     ⚠️ **展品不自己轉。** B3 之前這裡寫 `rotation.y = 展示台的 yaw`，
     而 `placeCodexCamera()` 又讓鏡頭繞著同一個 yaw 公轉 —— 兩者**完全抵消**，
     結果是拖曳只把房間（立柱、比例尺機體、燈）轉走，展品永遠正面對著鏡頭。
     實測：拖 180° 之後 KEIL 的斜面仍然朝向鏡頭，只有立柱換了位置。
     旋轉一律由鏡頭負責，這樣「正面／側面／背面」才有意義。 */
  cur.g.position.set(0, (o.ry || 0) * cur.sc, (o.rz || 0) * cur.sc);
  cur.g.rotation.set(o.rp || 0, 0, o.rr || 0);
}

/** 展品在畫面上佔了多少（驗收用，`__CHASSIS_DEBUG.codex()`）。
    回 `{ fh, fw }` = 包圍盒投影後佔掃描艙高／寬的比例。
    B3 的門檻是 `fh ≥ 0.60` —— 「取景太遠」只有量得出來才修得掉，
    用眼睛看每一張都覺得「還好」。 */
export function codexFrame() {
  if (!cur) return null;
  cur.g.updateMatrixWorld(true);
  _probe.setFromObject(cur.g);
  /* **包圍盒可能是無限大**（某些造型有沒設 boundingBox 的幾何），
     那時候投影出來是 ±10 億 —— 看起來像量測壞了，其實是量不到。 */
  if (!finiteBox(_probe)) return null;
  var lo = 1e9, hi = -1e9, le = 1e9, ri = -1e9, v = new THREE.Vector3();
  for (var i = 0; i < 8; i++) {
    v.set(i & 1 ? _probe.max.x : _probe.min.x, i & 2 ? _probe.max.y : _probe.min.y,
      i & 4 ? _probe.max.z : _probe.min.z);
    /* **有角落在鏡頭後面就不要算。** 透視投影在 w ≤ 0 時會翻號，
       量出來是 -10 億這種數字 —— 而那看起來像「量測壞了」，其實是
       「這一隻有一部分在鏡頭後面」，是取景真的有問題。照實回 null。 */
    v.applyMatrix4(camera.matrixWorldInverse);
    if (v.z > -0.05) return null;
    v.applyMatrix4(camera.projectionMatrix);
    if (v.y < lo) lo = v.y;
    if (v.y > hi) hi = v.y;
    if (v.x < le) le = v.x;
    if (v.x > ri) ri = v.x;
  }
  return { fh: +((hi - lo) / 2).toFixed(3), fw: +((ri - le) / 2).toFixed(3) };
}

/** 展品實際畫了幾顆 mesh。**Boss 的成本讀數** —— 骨段合併有沒有生效看它
    （規格 §4.3 給每一隻標了合併後的 mesh 數，對不上就是合併漏了）。 */
export function modelMeshes() {
  if (!cur) return 0;
  var n = 0;
  cur.g.traverse(function (o: any) { if (o.isMesh) n++; });
  return n;
}

/** 展示台上那一隻要不要顯示（群體壓力模式下它會擋住台子中央）。 */
export function setCodexModelVisible(v) { host.visible = v; }
